import { api } from '@/lib/api'
import type { Contractor, Employee, PayRun, Payline, Payslip } from './types'

export interface PaySchedule {
  id: string
  name: string
  frequency: string
  first_pay_date: string
  anchor_period_end: string
  pay_day_offset_days: number
  timezone: string
  currency: string
  bank_cutoff_hour: number
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface PayComponent {
  id: string
  slug: string
  label: string
  component_type: string
  calc_kind: string
  formula?: string | null
  fixed_amount_minor_units?: string | null
  fixed_currency?: string | null
  rate_pct?: string | null
  rounding_mode: string
  is_system: boolean
  effective_from: string
  effective_to?: string | null
}

export interface EmployeeCompensation {
  id: string
  revision_type: string
  pay_schedule_id: string | null
  base_salary_minor_units: string | null
  base_salary_currency: string | null
  hourly_rate_minor_units: string | null
  hourly_rate_currency: string | null
  standard_weekly_hours: string | null
  component_set: string[]
  effective_from: string
  effective_to: string | null
}

export interface CompensationInput {
  pay_type: 'salaried' | 'hourly'
  base_salary_minor_units?: number | null
  hourly_rate_minor_units?: number | null
  base_salary_currency?: string
  hourly_rate_currency?: string
  standard_weekly_hours?: number
  pay_schedule_id?: string | null
  effective_from?: string
  revision_type?: string
}

export interface DashboardTiles {
  active_employees: number
  pending_runs: number
  next_run: { id: string; run_number: string; pay_date: string; status: string } | null
  ytd_gross_minor_units: string
  open_anomalies: number
}

export async function getDashboardTiles() {
  return api.get<{ data: DashboardTiles }>('/api/payroll/dashboard/tiles')
}

export async function listEmployees(opts: { q?: string; status?: string; limit?: number; offset?: number } = {}) {
  const params = new URLSearchParams()
  if (opts.q) params.set('q', opts.q)
  if (opts.status) params.set('status', opts.status)
  if (opts.limit) params.set('limit', String(opts.limit))
  if (opts.offset) params.set('offset', String(opts.offset))
  const suffix = params.toString() ? `?${params}` : ''
  return api.get<{ data: Employee[] }>(`/api/payroll/employees${suffix}`)
}

export async function getEmployee(id: string) {
  return api.get<{ data: Employee; compensation: EmployeeCompensation | null }>(`/api/payroll/employees/${id}`)
}

export async function createEmployee(body: Partial<Employee> & { compensation?: CompensationInput }) {
  return api.post<{ data: Employee }>('/api/payroll/employees', body)
}

export async function updateEmployee(
  id: string,
  body: Partial<Employee> & { expected_updated_at: string; compensation?: CompensationInput },
) {
  return api.put<{ data: Employee }>(`/api/payroll/employees/${id}`, body)
}

export async function terminateEmployee(id: string, body: { termination_date: string; reason?: string }) {
  return api.post<{ data: Employee }>(`/api/payroll/employees/${id}/terminate`, body)
}

export interface EmployeeW4 {
  id: string
  filing_status: string
  multiple_jobs: boolean
  dependents_count: number
  dependents_credit_minor_units: string | null
  other_income_minor_units: string | null
  deductions_minor_units: string | null
  additional_withholding_minor_units: string | null
  exempt: boolean
  effective_from: string
  signed_at: string | null
  updated_at: string
}

export interface EmployeeW4Input {
  filing_status: string
  multiple_jobs?: boolean
  dependents_credit_minor_units?: number
  other_income_minor_units?: number
  deductions_minor_units?: number
  additional_withholding_minor_units?: number
  exempt?: boolean
}

export async function getEmployeeW4(id: string) {
  return api.get<{ data: EmployeeW4 | null }>(`/api/payroll/employees/${id}/w4`)
}

export async function updateEmployeeW4(id: string, body: EmployeeW4Input) {
  return api.put<{ data: EmployeeW4 }>(`/api/payroll/employees/${id}/w4`, body)
}

export interface EmployeeI9 {
  id: string
  work_authorization_type: string | null
  work_authorization_expiry: string | null
  i9_status: string
  i9_section1_completed_at: string | null
  i9_section2_completed_at: string | null
  i9_reverification_due: string | null
  everify_case_number: string | null
  everify_status: string
}

export interface EmployeeI9Input {
  work_authorization_type?: string | null
  work_authorization_expiry?: string | null
  i9_section1_completed_at?: string | null
  i9_section2_completed_at?: string | null
  everify_case_number?: string | null
  everify_status?: string
}

export async function getEmployeeI9(id: string) {
  return api.get<{ data: EmployeeI9 }>(`/api/payroll/employees/${id}/i9`)
}

export async function updateEmployeeI9(id: string, body: EmployeeI9Input) {
  return api.put<{ data: EmployeeI9 }>(`/api/payroll/employees/${id}/i9`, body)
}

export async function listContractors(opts: { q?: string; status?: string } = {}) {
  const params = new URLSearchParams()
  if (opts.q) params.set('q', opts.q)
  if (opts.status) params.set('status', opts.status)
  const suffix = params.toString() ? `?${params}` : ''
  return api.get<{ data: Contractor[] }>(`/api/payroll/contractors${suffix}`)
}

export async function getContractor(id: string) {
  return api.get<{ data: Contractor }>(`/api/payroll/contractors/${id}`)
}

export async function createContractor(body: Partial<Contractor>) {
  return api.post<{ data: Contractor }>('/api/payroll/contractors', body)
}

export async function listComponents(opts: { type?: string } = {}) {
  const params = new URLSearchParams()
  if (opts.type) params.set('type', opts.type)
  const suffix = params.toString() ? `?${params}` : ''
  return api.get<{ data: PayComponent[] }>(`/api/payroll/components${suffix}`)
}

export async function createComponent(body: Partial<PayComponent>) {
  return api.post<{ data: PayComponent }>('/api/payroll/components', body)
}

export async function setupUsComponents() {
  return api.post<{ data: { inserted: number; total: number } }>('/api/payroll/components/setup-us', {})
}

export async function payPayRun(id: string) {
  return api.post<{ data: { status: string; payment_count: number } }>(`/api/payroll/runs/${id}/pay`, {})
}

export async function listPaySchedules() {
  return api.get<{ data: PaySchedule[] }>('/api/payroll/pay-schedules')
}

export async function createPaySchedule(body: Partial<PaySchedule>) {
  return api.post<{ data: PaySchedule }>('/api/payroll/pay-schedules', body)
}

export async function listPayRuns(opts: { status?: string; limit?: number } = {}) {
  const params = new URLSearchParams()
  if (opts.status) params.set('status', opts.status)
  if (opts.limit) params.set('limit', String(opts.limit))
  const suffix = params.toString() ? `?${params}` : ''
  return api.get<{ data: PayRun[] }>(`/api/payroll/runs${suffix}`)
}

export async function getPayRun(id: string) {
  return api.get<{ data: PayRun; paylines: Payline[]; payslips: Payslip[] }>(`/api/payroll/runs/${id}`)
}

export async function createPayRun(body: {
  pay_schedule_id?: string | null
  period_start: string
  period_end: string
  pay_date: string
  run_type?: string
  currency?: string
}) {
  return api.post<{ data: PayRun }>('/api/payroll/runs', body)
}
